"use client";

import { useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import { Plus, FileUp } from "lucide-react";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TickerCombobox } from "@/components/portfolio/ticker-combobox";
import { createClient } from "@/lib/supabase/client";
import type { SecurityRef } from "@/lib/data/types";
import { CURRENCY_OPTIONS } from "@/lib/config";
import { cn } from "@/lib/utils";
import { parseDividendReceiptText } from "@/lib/dividend-tracker/import/parse-dividend-receipt-text";

const entrySchema = z.object({
  symbol: z.string().min(1, "Bitte eine Aktie auswählen."),
  companyName: z.string().min(1),
  amount: z.coerce.number().positive("Betrag muss größer als 0 sein."),
  currency: z.string().min(1),
  paymentDate: z
    .string()
    .min(1, "Bitte ein Datum angeben.")
    .refine((v) => v <= new Date().toISOString().slice(0, 10), "Datum darf nicht in der Zukunft liegen."),
});

/**
 * Manuelle Dividenden-Erfassung (Feedback-Runde 4, Punkt 19) - gleiches Muster wie
 * AddPositionDialog (Ticker-Suche + direkter Supabase-Insert, RLS regelt die
 * Berechtigung statt einer eigenen API-Route).
 */
export function AddDividendDialog() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<SecurityRef | null>(null);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  // Runde 8 Punkt 1+27 (Erik: "nebeneinander aufführen das Feld und man entscheidet
  // sich") - Default Netto (Eriks explizite Fallback-Annahme), da die eigene Dividende
  // meist schon netto ausgezahlt wird.
  const [isGross, setIsGross] = useState(false);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // PDF-Upload-Vorbefuellung (backlog.md -> "optionaler PDF-Upload/OCR", Erik-
  // Rueckfrage zu Kosten/Speicherort) - rein client-seitig, tickerQuery wird nur
  // vorbefuellt, NIE automatisch zu `selected` (siehe parse-dividend-receipt-text.ts,
  // companyGuess-Kommentar: keine automatische Zuordnung ohne Nutzerbestaetigung).
  const [tickerQuery, setTickerQuery] = useState("");
  const [isExtractingPdf, setIsExtractingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  function resetForm() {
    setSelected(null);
    setAmount("");
    setCurrency("USD");
    setIsGross(false);
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setError(null);
    setTickerQuery("");
    setPdfError(null);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) resetForm();
  }

  async function handlePdfUpload(file: File) {
    setPdfError(null);
    setIsExtractingPdf(true);
    try {
      // Dynamischer Import statt statischem Top-Level-Import (Bundle-Size-Audit, 2026-08-10)
      // - extract-pdf-text.ts zieht pdfjs-dist (~400 KB) nach, das sonst in jedes Bundle
      // faellt, das diesen Dialog rendert, obwohl es nur bei echtem PDF-Upload gebraucht wird.
      const { extractPdfText } = await import("@/lib/dividend-tracker/import/extract-pdf-text");
      const text = await extractPdfText(file);
      const guess = parseDividendReceiptText(text);
      if (guess.amount === undefined && guess.paymentDate === undefined && !guess.isin && !guess.companyGuess) {
        setPdfError("Konnte keine Angaben aus der PDF herauslesen - bitte manuell ausfüllen.");
        return;
      }
      if (guess.amount !== undefined) setAmount(String(guess.amount));
      if (guess.currency) setCurrency(guess.currency);
      if (guess.paymentDate) setPaymentDate(guess.paymentDate);
      if (guess.isGross !== undefined) setIsGross(guess.isGross);
      // ISIN (Feedback-Runde 19 C/13, 2026-07-28) bevorzugt vor dem lockereren
      // Ticker-Regex-Treffer - siehe DividendReceiptGuess.isin-Kommentar fuer die
      // Begruendung. TickerCombobox loest beides ueber dieselbe /api/search-Suche auf.
      if (guess.isin) setTickerQuery(guess.isin);
      else if (guess.companyGuess) setTickerQuery(guess.companyGuess);
    } catch {
      setPdfError("PDF konnte nicht gelesen werden - vermutlich kein Text enthalten (gescanntes Bild) oder beschädigt.");
    } finally {
      setIsExtractingPdf(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const parsed = entrySchema.safeParse({
      symbol: selected?.symbol ?? "",
      companyName: selected?.name ?? "",
      amount,
      currency,
      paymentDate,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Ungültige Eingabe.");
      return;
    }

    setIsPending(true);
    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setError("Bitte melde dich erneut an.");
      setIsPending(false);
      return;
    }

    const { error: insertError } = await supabase.from("dividend_events").insert({
      user_id: user.id,
      symbol: parsed.data.symbol,
      company_name: parsed.data.companyName,
      amount: parsed.data.amount,
      currency: parsed.data.currency,
      payment_date: parsed.data.paymentDate,
      is_gross: isGross,
    });

    setIsPending(false);
    if (insertError) {
      setError("Eintrag konnte nicht gespeichert werden.");
      return;
    }

    handleOpenChange(false);
    router.refresh();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button">
          <Plus />
          Dividende eintragen
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Dividende eintragen</DialogTitle>
          <DialogDescription>
            Aktie, erhaltener Betrag und Zahlungsdatum - der Rest (Auswertung nach
            Jahr/Quartal/Monat) läuft automatisch. Optional: Beleg als PDF hochladen,
            um die Felder automatisch befüllen zu lassen (rein im Browser, die Datei
            wird nicht gespeichert).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="dividend-pdf-input"
              className={cn(
                "flex cursor-pointer items-center justify-center gap-1.5 rounded-md border border-dashed border-hairline bg-muted/40 px-3 py-2 text-xs text-muted-foreground transition-colors hover:border-primary hover:bg-muted",
                isExtractingPdf && "pointer-events-none opacity-60",
              )}
            >
              <FileUp className="h-3.5 w-3.5" />
              {isExtractingPdf ? "Beleg wird gelesen…" : "Beleg (PDF) hochladen – befüllt die Felder unten automatisch"}
            </label>
            <input
              id="dividend-pdf-input"
              type="file"
              accept=".pdf,application/pdf"
              disabled={isExtractingPdf}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void handlePdfUpload(file);
              }}
              className="sr-only"
            />
            {pdfError && <p className="text-xs text-negative">{pdfError}</p>}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-ink-secondary">Aktie oder ETF</label>
            <TickerCombobox
              value={tickerQuery}
              onQueryChange={setTickerQuery}
              onSelect={async (ref) => {
                setSelected(ref);
                try {
                  const res = await fetch("/api/recent-scores", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ symbols: [ref.symbol] }),
                  });
                  if (!res.ok) return;
                  const [summary] = (await res.json()) as { currency?: string }[];
                  if (summary?.currency) setCurrency(summary.currency);
                } catch {
                  // Waehrungs-Vorbefuellung ist nur Komfort, bleibt manuell editierbar.
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-medium text-ink-secondary">Betrag</label>
              {/* Runde 8 Punkt 1+27: Feld und Umschalter nebeneinander, Nutzer
                  entscheidet direkt beim Eintragen statt einer festen Annahme. */}
              <div className="flex shrink-0 gap-1 rounded-full bg-muted p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setIsGross(false)}
                  className={cn(
                    "rounded-full px-2.5 py-0.5 font-medium transition-colors",
                    !isGross ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
                  )}
                >
                  Netto
                </button>
                <button
                  type="button"
                  onClick={() => setIsGross(true)}
                  className={cn(
                    "rounded-full px-2.5 py-0.5 font-medium transition-colors",
                    isGross ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
                  )}
                >
                  Brutto
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                type="number"
                min="0"
                step="any"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="z. B. 12.50"
                className="bg-card"
              />
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-24 shrink-0 rounded-md border border-hairline bg-card px-2 text-sm text-ink"
              >
                {/* Vom Ticker vorbefuellte Waehrung immer als Option verfuegbar, auch
                    falls sie nicht in der kuratierten Liste steht. */}
                {!CURRENCY_OPTIONS.includes(currency as (typeof CURRENCY_OPTIONS)[number]) && (
                  <option value={currency}>{currency}</option>
                )}
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-ink-secondary">Zahlungsdatum</label>
            <Input
              type="date"
              max={new Date().toISOString().slice(0, 10)}
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="bg-card"
            />
          </div>
          <Button type="submit" disabled={isPending}>
            {isPending ? "Einen Moment…" : "Speichern"}
          </Button>
          {error && <p className="text-xs text-negative">{error}</p>}
        </form>
      </DialogContent>
    </Dialog>
  );
}
